import { useCallback, useMemo, useState } from 'react';
import { Chip } from '@mui/material';

import type {
  BillingTaskCategory,
  BillingTaskItem,
  BillingTaskStatus,
  BillingTaskSubjectType,
} from '../store/billingTasksApi';

import { useOrgBillingTasksQuery } from '../store/hooks';
import { formatRelativeTime } from '../utils/formatting';

import { FilterableDataTable } from './filterable-data-table';

type ChipColor = 'default' | 'error' | 'info' | 'success' | 'warning';

const STATUS_CHIP_COLORS: Record<string, ChipColor> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  dismissed: 'default',
};

const SUBJECT_LABELS: Record<string, string> = {
  claim: 'Claim',
  remittance_claim: 'Remittance',
};

const FILTERS = [
  {
    key: 'status',
    label: 'Status',
    type: 'select' as const,
    options: [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In progress' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'dismissed', label: 'Dismissed' },
    ],
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select' as const,
    options: [
      { value: 'manual_review', label: 'Manual review' },
      { value: 'era_attention', label: 'ERA attention' },
      { value: 'withheld_posting', label: 'Withheld posting' },
    ],
  },
  {
    key: 'subject_type',
    label: 'Subject',
    type: 'select' as const,
    options: [
      { value: 'claim', label: 'Claim' },
      { value: 'remittance_claim', label: 'Remittance claim' },
    ],
  },
];

interface OrganizationBillingTasksSectionProps {
  organizationId: string;
}

/**
 * The org's billing-task queue (GDN-2644) inside the organization
 * workspace: per-status segment chips and the filterable task table.
 */
export function OrganizationBillingTasksSection({
  organizationId,
}: OrganizationBillingTasksSectionProps) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { data, isLoading } = useOrgBillingTasksQuery(
    {
      organizationId,
      page,
      perPage,
      status: (filters.status || undefined) as BillingTaskStatus | undefined,
      category: (filters.category || undefined) as BillingTaskCategory | undefined,
      subjectType: (filters.subject_type || undefined) as
        | BillingTaskSubjectType
        | undefined,
    },
    { skip: !organizationId },
  );

  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'created_at' as const,
        label: 'Created',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-600 whitespace-nowrap">
            {formatRelativeTime(value ? String(value) : null)}
          </span>
        ),
      },
      {
        key: 'subject_type' as const,
        label: 'Subject',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-900">
            {SUBJECT_LABELS[String(value)] || String(value)}
          </span>
        ),
      },
      {
        key: 'category_label' as const,
        label: 'Category',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-900">{String(value)}</span>
        ),
      },
      {
        key: 'reason' as const,
        label: 'What needs doing',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-900">{String(value)}</span>
        ),
      },
      {
        key: 'status' as const,
        label: 'Status',
        sortable: false,
        render: (value: unknown, row: BillingTaskItem) => (
          <Chip
            label={row.status_label}
            size="small"
            color={STATUS_CHIP_COLORS[String(value)] || 'default'}
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        key: 'resolution_note' as const,
        label: 'Resolution',
        sortable: false,
        render: (value: unknown) => (
          <span className="text-gray-600">{value ? String(value) : '—'}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      {data && (
        <div className="flex gap-2 mb-4">
          {(data.segments ?? []).map(segment => (
            <Chip
              key={segment.key}
              label={`${segment.label}: ${segment.count}`}
              size="small"
              color={STATUS_CHIP_COLORS[segment.key] || 'default'}
              variant={segment.count > 0 ? 'filled' : 'outlined'}
            />
          ))}
        </div>
      )}

      <FilterableDataTable<BillingTaskItem>
        title="Billing Tasks"
        columns={columns}
        data={data?.items ?? []}
        total={data?.total}
        loading={isLoading}
        page={page}
        perPage={perPage}
        filters={FILTERS}
        onPageChange={setPage}
        onPerPageChange={handlePerPageChange}
        onFiltersChange={handleFiltersChange}
        emptyMessage="No billing tasks match these filters"
      />
    </div>
  );
}
